import { Flex, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { cloneDeep, isEqual } from "lodash";
import { useCallback } from "react";
import {
  DefaultNotificationSettingsDto,
  RecurringContractReminderDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { useSaveOrganization } from "../../organizations/current/useOrganizationEditor";
import { DeleteSettingButton } from "./DeleteSettingButton";
import { initSettings } from "./NotificationSettings";
import { useReceiverRoleNames } from "./useReceiverRoleNames";

export const RecurringContractReminderSetting = ({ setting }: { setting: RecurringContractReminderDto }) => {
  const roleName = useReceiverRoleNames();
  const authState = useLoggedInWithOrgContextState();
  const { trigger } = useApiError();

  const { saveOrganization, isSaving } = useSaveOrganization();

  const leadTimeUnits = {
    Days: t("days"),
    Months: t("months"),
  };

  const deleteSetting = useCallback(async () => {
    const settings: DefaultNotificationSettingsDto =
      cloneDeep(authState.selectedOrg.defaultNotificationSettings) ?? initSettings();
    settings.contractNotificationSettings.noEndDateNotifications =
      settings.contractNotificationSettings.noEndDateNotifications.filter((s) => !isEqual(s, setting));
    try {
      await saveOrganization({ defaultNotificationSettings: settings });
    } catch (e) {
      trigger(e);
    }
  }, [authState.selectedOrg.defaultNotificationSettings, trigger, setting, saveOrganization]);

  return (
    <Flex flexDirection={"column"} fontSize={"sm"} backgroundColor={"gray.50"} rounded="md" p="2" my="1">
      <Flex justifyContent={"space-between"} width="full">
        <Text>{t("Interval")}</Text>
        <DeleteSettingButton isLoading={isSaving} onClick={() => deleteSetting()} />
      </Flex>
      <Text fontWeight={"bold"}>
        {setting.interval.amount} {leadTimeUnits[setting.interval.unit]}
      </Text>
      <Text>{t("Individual receivers")}</Text>
      {setting.receivers.persons.map((person) => (
        <Text key={person.id} fontWeight={"bold"}>
          {person.firstName} {person.lastName}
        </Text>
      ))}
      <Text>{t("Organization role receivers")}</Text>
      {setting.receivers.organizationRoles.map((role) => (
        <Text key={role} fontWeight={"bold"}>
          {roleName.organizationRole(role)}
        </Text>
      ))}
      <Text>{t("Contract role receivers")}</Text>
      {setting.receivers.contractRoles.map((role) => (
        <Text key={role} fontWeight={"bold"}>
          {roleName.contractRole(role)}
        </Text>
      ))}
    </Flex>
  );
};
